import { useState } from "react";
import { CalculationOutput } from "../types";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";

export default function FinancialSummary({
  output,
  periodMode,
  isPremium = true,
}: {
  output: CalculationOutput | null;
  periodMode: PeriodMode;
  isPremium?: boolean;
}) {
  const [summaryMode, setSummaryMode] = useState<"cash" | "fiscal">("cash");

  if (!output) return null;

  const fmtByPeriod = (annual: number) => `${fmtEUR(annual, periodMode)}${suffix(periodMode)}`;
  const fmtPct = (value: number) => `${(value || 0).toFixed(1)}%`;

  const bruto = (output as any).brutoFacturado ?? 0;
  const costes = (output as any).costesCash ?? 0;
  const reta = (output as any).cuotaRETAAnual ?? 0;
  const rendNeto = output.rendimientoNeto ?? 0;
  const irpfTotal = (output as any).irpfTotal ?? output.irpf?.cuotaTotal ?? 0;
  const netoFiscal = output.netoFiscalAnual ?? rendNeto - irpfTotal;
  const caja = (output as any).cajaPreviaRenta ?? 0;
  const netoFinal = (output as any).netoFinalTrasRenta ?? netoFiscal;
  const anticipos = (output as any).anticiposIRPF ?? 0;
  const irpfRegularizacion = (output as any).irpfRegularizacion ?? irpfTotal - anticipos;
  const pctBruto = bruto > 0 ? (output.irpfPctSobreBruto ?? ((irpfTotal / bruto) * 100)) : 0;

  const netoShare = bruto > 0 ? Math.round((netoFinal / bruto) * 100) : 0;
  const irpfShare = bruto > 0 ? Math.round((irpfTotal / bruto) * 100) : 0;
  const retaShare = bruto > 0 ? Math.round((reta / bruto) * 100) : 0;
  const costShare = bruto > 0 ? Math.round((costes / bruto) * 100) : 0;

  const cards =
    summaryMode === "cash"
      ? [
          {
            label: "Caja previa",
            value: fmtByPeriod(caja),
            sub: "Caja al final del año antes de hacer la declaración.",
            primary: true,
          },
          {
            label: "IRPF total",
            value: fmtByPeriod(irpfTotal),
            sub: "Cuota total estimada del IRPF anual.",
          },
          {
            label: "Anticipos",
            value: fmtByPeriod(anticipos),
            sub: "Retenciones y modelo 130 ya pagados durante el año.",
          },
          {
            label: "Regularización renta",
            value: fmtByPeriod(irpfRegularizacion),
            sub: "Ajuste final en la renta: positivo pagas, negativo te devuelven.",
          },
          {
            label: "Rendimiento neto",
            value: fmtByPeriod(rendNeto),
            sub: "Antes de IRPF, tras gastos deducibles y RETA.",
          },
        ]
      : [
          {
            label: "Rend. neto",
            value: fmtByPeriod(rendNeto),
            sub: "Antes de IRPF.",
            primary: true,
          },
          {
            label: "IRPF total",
            value: fmtByPeriod(irpfTotal),
            sub: "Cuota teórica anual.",
          },
          {
            label: "Neto fiscal",
            value: fmtByPeriod(netoFiscal),
            sub: "Rendimiento neto menos IRPF.",
          },
          {
            label: "IRPF / bruto",
            value: bruto > 0 ? fmtPct(pctBruto) : "—",
            sub: "Tipo efectivo sobre facturación.",
          },
        ];

  return (
    <aside className="summary-panel">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div>
          <div className="results-card-title" style={{ fontSize: 22, marginBottom: 4 }}>
            Resumen financiero
          </div>
          <div className="sub">Lo importante, de un vistazo.</div>
        </div>

        {isPremium && (
          <div className="svt-toggle">
            <button
              type="button"
              className={`svt-btn${summaryMode === "cash" ? " on" : ""}`}
              onClick={() => setSummaryMode("cash")}
            >
              Caja
            </button>
            <button
              type="button"
              className={`svt-btn${summaryMode === "fiscal" ? " on" : ""}`}
              onClick={() => setSummaryMode("fiscal")}
            >
              Fiscal
            </button>
          </div>
        )}
      </div>

      <div className="metric-card primary" style={{ marginBottom: 12 }}>
        <div className="metric-label">Neto final estimado</div>
        <div className="kpi-main-value">{fmtByPeriod(netoFinal)}</div>
        <div className="metric-sub">Distribución de tu bruto después de costes, RETA e IRPF.</div>

        <div className="stackbar">
          <div className="sb-neto" style={{ width: `${Math.max(netoShare, 0)}%` }} />
          <div className="sb-irpf" style={{ width: `${Math.max(irpfShare, 0)}%` }} />
          <div className="sb-reta" style={{ width: `${Math.max(retaShare, 0)}%` }} />
          <div className="sb-otros" style={costShare > 0 ? { width: `${costShare}%` } : { flex: 1 }} />
        </div>

        <div className="stackbar-legend">
          <span className="legend-item">
            <span className="legend-dot sb-neto" />
            Neto
          </span>
          <span className="legend-item">
            <span className="legend-dot sb-irpf" />
            IRPF
          </span>
          <span className="legend-item">
            <span className="legend-dot sb-reta" />
            RETA
          </span>
          <span className="legend-item">
            <span className="legend-dot sb-otros" />
            Costes
          </span>
        </div>
      </div>

      <div className="summary-grid">
        {cards.map((card) => (
          <div key={card.label} className={`metric-card${card.primary ? " primary" : ""}`}>
            <div className="metric-label">{card.label}</div>
            <div className={card.primary ? "kpi-main-value" : "kpi-value"}>{card.value}</div>
            <div className="metric-sub">{card.sub}</div>
          </div>
        ))}
      </div>

      {!isPremium && (
        <div
          style={{
            marginTop: 16,
            padding: "10px 14px",
            borderRadius: 8,
            background: "var(--amber-bg)",
            border: "1px solid rgba(154,91,0,.15)",
            fontSize: 11.5,
            color: "var(--amber)",
            lineHeight: 1.5,
          }}
        >
          Versión simplificada. <strong>Alta Premium (gratis)</strong> para ver detalle
          de IRPF, IVA, trimestres y comparación de escenarios.
        </div>
      )}
    </aside>
  );
}
